/**
 * PriceMatrixEnhanced - Cross-venue price comparison table with row selection.
 *
 * Based on PriceMatrix but receives games as props and supports
 * click-to-select for driving downstream panels (BookDepth, Microstructure).
 */

import type { GameRow, VenueBook } from '@/views/ValueDashboard/types';

// ── Book helpers ─────────────────────────────────────────────────

function bestBid(book: VenueBook | undefined | null): number | null {
  if (!book?.yes?.length) return null;
  return Math.max(...book.yes.map((l) => l.priceCents));
}

function bestAsk(book: VenueBook | undefined | null): number | null {
  if (!book?.no?.length) return null;
  return 100 - Math.max(...book.no.map((l) => l.priceCents));
}

function spread(book: VenueBook | undefined | null): number | null {
  const bid = bestBid(book);
  const ask = bestAsk(book);
  if (bid === null || ask === null) return null;
  return ask - bid;
}

function mid(book: VenueBook | undefined | null): number | null {
  const bid = bestBid(book);
  const ask = bestAsk(book);
  if (bid === null || ask === null) return null;
  return (bid + ask) / 2;
}

function fmtCents(c: number | null): string {
  if (c === null) return '\u2014';
  return `${c.toFixed(1)}\u00A2`;
}

function fmtSpread(c: number | null): string {
  if (c === null) return '\u2014';
  return `${c.toFixed(1)}`;
}

// ── Row data ─────────────────────────────────────────────────────

interface RowData {
  game: GameRow;
  side: 'away' | 'home';
  label: string;
  kalshiBid: number | null;
  kalshiAsk: number | null;
  kalshiSpread: number | null;
  polyBid: number | null;
  polyAsk: number | null;
  polySpread: number | null;
  divergence: number | null;
}

function buildRows(games: GameRow[]): RowData[] {
  const rows: RowData[] = [];
  for (const game of games) {
    for (const side of ['away', 'home'] as const) {
      const kBook = game.kalshi?.[side];
      const pBook = game.polymarket?.[side];
      const kMid = mid(kBook);
      const pMid = mid(pBook);
      const div = kMid !== null && pMid !== null ? Math.abs(kMid - pMid) : null;

      rows.push({
        game,
        side,
        label: side === 'away' ? game.awayName || game.awayCode : game.homeName || game.homeCode,
        kalshiBid: bestBid(kBook),
        kalshiAsk: bestAsk(kBook),
        kalshiSpread: spread(kBook),
        polyBid: bestBid(pBook),
        polyAsk: bestAsk(pBook),
        polySpread: spread(pBook),
        divergence: div,
      });
    }
  }
  return rows;
}

// ── Component ────────────────────────────────────────────────────

interface PriceMatrixEnhancedProps {
  games: GameRow[];
  selectedGameKey: string | null;
  onSelectGame: (key: string) => void;
}

export function PriceMatrixEnhanced({ games, selectedGameKey, onSelectGame }: PriceMatrixEnhancedProps) {
  if (games.length === 0) {
    return (
      <div className="text-muted-foreground flex h-full items-center justify-center text-sm">
        Waiting for market data...
      </div>
    );
  }

  const rows = buildRows(games);

  return (
    <div className="h-full overflow-auto">
      <table className="w-full text-xs">
        <thead className="bg-muted/50 sticky top-0">
          <tr>
            <th className="border-border border-b px-2 py-1.5 text-left font-medium">Game</th>
            <th className="border-border border-b px-2 py-1.5 text-left font-medium">Team</th>
            <th className="border-border border-b px-2 py-1.5 text-right font-medium">K Bid</th>
            <th className="border-border border-b px-2 py-1.5 text-right font-medium">K Ask</th>
            <th className="border-border border-b px-2 py-1.5 text-right font-medium">K Spd</th>
            <th className="border-border border-b px-2 py-1.5 text-right font-medium">P Bid</th>
            <th className="border-border border-b px-2 py-1.5 text-right font-medium">P Ask</th>
            <th className="border-border border-b px-2 py-1.5 text-right font-medium">P Spd</th>
            <th className="border-border border-b px-2 py-1.5 text-right font-medium">Div</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const isHighDiv = r.divergence !== null && r.divergence >= 3;
            const isSelected = r.game.key === selectedGameKey;

            let rowClass = 'cursor-pointer transition-colors';
            if (isSelected) {
              rowClass += ' ring-1 ring-primary/50 bg-primary/5';
            } else if (isHighDiv) {
              rowClass += ' bg-yellow-500/10 hover:bg-yellow-500/20';
            } else {
              rowClass += ' hover:bg-muted/30';
            }

            return (
              <tr
                key={`${r.game.key}-${r.side}`}
                className={rowClass}
                onClick={() => onSelectGame(r.game.key)}
              >
                <td className="border-border/50 border-b px-2 py-1 text-muted-foreground whitespace-nowrap">
                  {r.side === 'away' ? `${r.game.awayCode}@${r.game.homeCode}` : ''}
                </td>
                <td className="border-border/50 border-b px-2 py-1 font-medium whitespace-nowrap">
                  {r.label}
                </td>
                <td className="border-border/50 border-b px-2 py-1 text-right tabular-nums text-green-400">
                  {fmtCents(r.kalshiBid)}
                </td>
                <td className="border-border/50 border-b px-2 py-1 text-right tabular-nums text-red-400">
                  {fmtCents(r.kalshiAsk)}
                </td>
                <td className="border-border/50 border-b px-2 py-1 text-right tabular-nums text-muted-foreground">
                  {fmtSpread(r.kalshiSpread)}
                </td>
                <td className="border-border/50 border-b px-2 py-1 text-right tabular-nums text-green-400">
                  {fmtCents(r.polyBid)}
                </td>
                <td className="border-border/50 border-b px-2 py-1 text-right tabular-nums text-red-400">
                  {fmtCents(r.polyAsk)}
                </td>
                <td className="border-border/50 border-b px-2 py-1 text-right tabular-nums text-muted-foreground">
                  {fmtSpread(r.polySpread)}
                </td>
                <td
                  className={`border-border/50 border-b px-2 py-1 text-right tabular-nums font-medium ${
                    isHighDiv ? 'text-yellow-400' : 'text-muted-foreground'
                  }`}
                >
                  {fmtSpread(r.divergence)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
